import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FiDroplet, FiArrowRight, FiX } from "react-icons/fi";
import { FaRegCalendarAlt, FaHeartbeat } from "react-icons/fa";
import { GiHealthNormal, GiSpotedFlower } from "react-icons/gi";

const ArticlePopup = () => {
  const [showPopup, setShowPopup] = useState(false);
  const currentDate = new Date();
  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const currentMonth = monthNames[currentDate.getMonth()];
  const currentYear = currentDate.getFullYear();

  useEffect(() => {
    const hasSeenPopup = sessionStorage.getItem("hasSeenWelcomePopup");
    if (!hasSeenPopup) {
      setTimeout(() => setShowPopup(true), 1000); // Slight delay for better UX
      sessionStorage.setItem("hasSeenWelcomePopup", "true");
    }
  }, []);

  const closePopup = () => {
    setShowPopup(false);
  };

  return (
    <AnimatePresence>
      {showPopup && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="fixed inset-0 flex items-center justify-center z-50 bg-black/50 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.8, y: 20, opacity: 0 }}
            animate={{ 
              scale: 1,
              y: 0,
              opacity: 1,
              transition: { 
                type: "spring",
                damping: 20,
                stiffness: 300
              }
            }}
            exit={{ 
              scale: 0.9,
              y: 10,
              opacity: 0,
              transition: { duration: 0.2 }
            }}
            className="relative bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-8 max-w-md mx-4 shadow-2xl border border-purple-100 overflow-hidden"
          >
            {/* Floating decorative elements */}
            <motion.div 
              animate={{
                y: [0, -5, 0],
                transition: {
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }
              }}
              className="absolute top-10 -left-6 text-pink-300/40"
            >
              <GiSpotedFlower className="text-5xl rotate-45" />
            </motion.div>
            
            <motion.div 
              animate={{
                y: [0, 5, 0],
                transition: {
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.5
                }
              }}
              className="absolute bottom-8 -right-6 text-purple-300/40"
            >
              <FaHeartbeat className="text-4xl -rotate-12" />
            </motion.div>

            {/* Glow effects */}
            <div className="absolute -top-4 -right-4 w-16 h-16 rounded-full bg-pink-200/50 blur-xl"></div>
            <div className="absolute -bottom-4 -left-4 w-20 h-20 rounded-full bg-purple-200/50 blur-xl"></div>
            
            {/* Close button */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={closePopup}
              className="absolute top-4 right-4 text-purple-500 hover:text-purple-700 transition-colors z-10"
            >
              <FiX className="w-6 h-6" />
            </motion.button>
            
            {/* Header */}
            <motion.div 
              initial={{ y: -10, opacity: 0 }}
              animate={{ 
                y: 0,
                opacity: 1,
                transition: { delay: 0.2 }
              }}
              className="flex flex-col items-center text-center mb-6 relative z-10"
            >
              <motion.div 
                whileHover={{ rotate: 5, scale: 1.05 }}
                className="relative mb-4"
              >
                <div className="absolute -inset-4 bg-pink-200/30 rounded-full blur-md"></div>
                <motion.div 
                  animate={{
                    rotate: [0, 5, 0],
                    transition: {
                      duration: 6,
                      repeat: Infinity,
                      repeatType: "reverse"
                    }
                  }}
                  className="relative p-4 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 text-white shadow-lg"
                >
                  <GiHealthNormal className="text-3xl" />
                </motion.div>
              </motion.div>
              <h2 className="text-3xl font-bold text-purple-900 font-cormorant">
                Welcome to Our Community!
              </h2>
              <div className="flex items-center mt-2 text-purple-600">
                <FaRegCalendarAlt className="mr-2" />
                <span className="font-medium">{currentMonth} {currentYear} Updates</span>
              </div>
            </motion.div>
            
            {/* Content */}
            <motion.div 
              initial={{ y: 10, opacity: 0 }}
              animate={{ 
                y: 0,
                opacity: 1,
                transition: { delay: 0.3 }
              }}
              className="mb-6 relative z-10"
            >
              <p className="text-purple-800 font-nunito leading-relaxed mb-4">
                We're excited to share our latest women's health articles and resources with you this {currentMonth}!
              </p>
              <ul className="space-y-2 text-purple-700">
                {[
                  "New cycle tracking techniques for 2025",
                  "Fertility nutrition research updates",
                  "Pregnancy wellness guides",
                  "Mental health during menstruation"
                ].map((item, index) => (
                  <motion.li 
                    key={index}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ 
                      x: 0,
                      opacity: 1,
                      transition: { delay: 0.4 + index * 0.1 }
                    }}
                    className="flex items-start"
                  >
                    <span className="text-pink-500 mr-2">•</span>
                    <span>{item}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
            
            {/* CTA */}
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ 
                y: 0,
                opacity: 1,
                transition: { delay: 0.7 }
              }}
              className="relative z-10"
            >
              <Link
                to="/blog"
                onClick={closePopup}
                className="group inline-flex items-center justify-center w-full py-3 px-6 rounded-xl bg-gradient-to-r from-pink-500 to-purple-500 text-white font-medium shadow-lg hover:shadow-xl transition-all hover:scale-[1.02] relative overflow-hidden"
              >
                {/* Button shine effect */}
                <span className="absolute inset-0 bg-gradient-to-r from-white/20 via-white/0 to-white/20 w-[50%] skew-x-[-20deg] shine-animation"></span>
                Explore Articles
                <FiArrowRight className="ml-2 transition-transform group-hover:translate-x-1" />
              </Link>
            </motion.div>
            
            {/* Footer note */}
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ 
                opacity: 1,
                transition: { delay: 0.8 }
              }}
              className="mt-4 text-center text-sm text-purple-500 relative z-10"
            >
              You won't see this again during this session
            </motion.p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};


export default ArticlePopup;